/**
 * Unwrap `$...$` / `$$...$$` around English, currency, and HTML so MathJax
 * does not italic-smash prose or show stray `$` glyphs.
 *
 * Runs for every exam that uses prepareExamHtml (not only TSA / tsaSitting).
 * Keep genuine TeX (`\frac`, `x^2`) and digit inequalities (`−12 < k < 15`).
 * Do not treat HTML or TSA comparisons (`A > B`) as math because of `<>`.
 *
 * Flip LATEX_TEXT_MODIFIERS_ENABLED to true to restore unwrap. Call sites stay.
 */

/** False: leave stored `$` / HTML alone. True: restore prose unwrap. */
export const LATEX_TEXT_MODIFIERS_ENABLED = false;

/** Keep in sync with backend `_TEX_CMD`. `\lt`/`\gt` are HTML-safe `<`/`>`. */
export const LATEX_CONTROL = /\\(?:frac|dfrac|tfrac|sqrt|sin|cos|tan|log|ln|theta|alpha|beta|gamma|pi|pm|mp|times|div|lt|gt|ll|gg|le|ge|leq|geq|leqslant|geqslant|nless|ngtr|neq|approx|infty|int|sum|prod|binom|partial|Delta|Sigma|equiv|subset|subseteq|in|notin|left|right|begin|end|text|mathrm|mathbf|cdot|ldots|quad|qquad|overline|underline|hat|bar|vec|to|rightarrow|leftarrow)\b/;

export function isHtmlBlob(text) {
  const s = String(text ?? '').trimStart();
  if (s.length < 2 || s[0] !== '<') return false;
  return /[A-Za-z/!?]/.test(s[1]);
}

function spanLooksLikeMath(inner) {
  const text = String(inner || '');
  if (isHtmlBlob(text)) return false;
  if (LATEX_CONTROL.test(text)) return true;
  if (/[_^]/.test(text)) return true;
  if ((text.includes('<') || text.includes('>')) && /\d/.test(text)) return true;
  return false;
}

function stripOuterHtmlDollarWrapper(text) {
  const match = String(text).match(/^(\s*)([\s\S]*?)(\s*)$/);
  const lead = match[1];
  const trail = match[3];
  const trimmed = match[2];
  if (trimmed.startsWith('$$') && trimmed.endsWith('$$') && trimmed.length >= 4) {
    const inner = trimmed.slice(2, -2);
    if (isHtmlBlob(inner)) return `${lead}${inner}${trail}`;
  } else if (trimmed.startsWith('$') && trimmed.endsWith('$') && trimmed.length >= 2) {
    const inner = trimmed.slice(1, -1);
    if (isHtmlBlob(inner)) return `${lead}${inner}${trail}`;
  }
  return text;
}

function unwrapNonMathDollars(text) {
  let next = stripOuterHtmlDollarWrapper(String(text));
  next = next.replace(/\$\$([\s\S]*?)\$\$/g, (full, inner) => (
    isHtmlBlob(inner) || !spanLooksLikeMath(inner) ? inner : full
  ));
  next = next.replace(/\$([^$]+)\$/g, (full, inner) => {
    if (isHtmlBlob(inner)) return inner;
    return spanLooksLikeMath(inner) ? full : inner;
  });
  return next;
}

export function sanitizeTsaProseMath(text) {
  if (!text || typeof text !== 'string') return text || '';
  if (!LATEX_TEXT_MODIFIERS_ENABLED) return text;
  return unwrapNonMathDollars(text);
}

export const sanitizeExamProseMath = sanitizeTsaProseMath;
